import React from 'react';
import { Sentry } from '../lib/sentry';
import { isChunkLoadError, shouldReloadForChunk } from '../lib/chunkError';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  /** A stale route chunk after a deploy: the tab is reloading itself. */
  reloading?: boolean;
}

/**
 * Top-level React ErrorBoundary (AUDIT-071).
 *
 * Catches render/lifecycle errors that would otherwise blank the React
 * root. Reports to Sentry when DSN is configured (AUDIT-079 wiring), then
 * shows a Hebrew-friendly "something went wrong" screen with a reload CTA.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
    // A deploy renamed the route chunks under this tab: not a bug, a stale page.
    // Reload once; if it fails again within a minute, show the error instead.
    if (isChunkLoadError(error) && shouldReloadForChunk()) {
      this.setState({ reloading: true });
      window.location.reload();
      return;
    }
    try {
      Sentry.withScope((scope) => {
        scope.setExtras({ componentStack: info.componentStack });
        Sentry.captureException(error);
      });
    } catch {
      /* Sentry may not be initialized in dev — don't mask original error */
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.state.reloading) {
      return (
        <div dir="rtl" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', background: '#fafbff' }}>
          המערכת התעדכנה, טוען את הגרסה החדשה…
        </div>
      );
    }

    const stale = isChunkLoadError(this.state.error);
    return (
      <div
        dir="rtl"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          fontFamily: 'inherit',
          color: '#222',
          background: '#fafbff',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 22, fontWeight: 600 }}>משהו השתבש</div>
        <div style={{ color: '#555', maxWidth: 480 }}>
          {stale
            ? 'המערכת התעדכנה בזמן שהדף היה פתוח, והטעינה מחדש לא הצליחה. נסה שוב בעוד רגע.'
            : 'אירעה שגיאה בלתי צפויה. ניסינו לדווח על הבעיה אוטומטית — נא נסה לטעון מחדש.'}
        </div>
        <button
          type="button"
          onClick={() => {
            // Full reload so route state + cached chunks are refreshed.
            window.location.reload();
          }}
          style={{
            padding: '10px 24px',
            borderRadius: 8,
            border: 'none',
            background: '#2d6fe6',
            color: '#fff',
            cursor: 'pointer',
            fontSize: 15,
          }}
        >
          טען מחדש
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
